import type { IconName } from '@w7/ui'

export interface NavItem {
  id: string
  label: string
  to: string
  icon: IconName
  description: string
  /** Keep route/page wired; omit from the rail menu. */
  hiddenFromNav?: boolean
}

export interface NavGroup {
  id: string
  label: string
  /** Optional group toggle icon; defaults to the first item’s icon. */
  icon?: IconName
  items: NavItem[]
}

/**
 * Exact-match NavLink when another rail item lives under this path
 * (e.g. `/bet-ticker` must not stay active on `/bet-ticker-flag-final`).
 */
export function navLinkEnd(to: string, allTos: readonly string[] = allNavPaths()): boolean {
  if (to === '/') return true
  const slashPrefix = to.endsWith('/') ? to : `${to}/`
  return allTos.some(
    (other) =>
      other !== to &&
      (other.startsWith(slashPrefix) || (other.startsWith(to) && other.length > to.length)),
  )
}

export function allNavPaths(): string[] {
  return NAV.flatMap((g) => g.items.map((i) => i.to))
}

/** Rail group that owns `pathname` (same match rules as NavLink + navLinkEnd). */
export function activeNavGroupId(
  groups: NavGroup[],
  pathname: string,
  navPaths: readonly string[] = allNavPaths(),
): string | null {
  for (const group of groups) {
    for (const item of group.items) {
      const exact = navLinkEnd(item.to, navPaths)
      const match = exact
        ? pathname === item.to
        : pathname === item.to || pathname.startsWith(`${item.to}/`)
      if (match) return group.id
    }
  }
  return null
}

/**
 * Rail grouped by question (Backend-UI / Strix pattern).
 * Lab CM Limits (Max Limits + Volume Percent) live under Bet settings.
 */
export const NAV: NavGroup[] = [
  {
    id: 'home',
    label: 'Home',
    items: [
      {
        id: 'home',
        label: 'Home',
        to: '/home',
        icon: 'overview',
        description: 'CM console home',
      },
    ],
  },
  {
    id: 'hierarchy',
    label: 'Member',
    items: [
      {
        id: 'portal-hierarchy',
        label: 'Portal hierarchy',
        to: '/hierarchy',
        icon: 'tree',
        description: 'Tree view · give/take credit',
      },
      {
        id: 'downline-list',
        label: 'Downline list',
        to: '/hierarchy/list',
        icon: 'downline',
        description: 'Member List · Take/Give settle · edit direct children',
      },
      {
        id: 'hierarchy-search',
        label: 'Hierarchy Search',
        to: '/hierarchy-search',
        icon: 'search',
        description: 'Lab /hierarchy-search — find downline · upline chain table',
      },
      {
        id: 'create-downline',
        label: 'Create Triple Master',
        to: '/hierarchy/create',
        icon: 'user',
        description: 'Credit limit · share · fancy/BM commission',
      },
      {
        id: 'user-access',
        label: 'User Access Level',
        to: '/users',
        icon: 'users',
        description: 'Staff logins · per-action grants under this CM',
      },
      {
        id: 'manage-access-roles',
        label: 'Manage Access Roles',
        to: '/users/access-roles',
        icon: 'users',
        description: 'Company Master pages & features (FP roleslists)',
      },
      {
        id: 'aggregate-members',
        label: 'Aggregate Members',
        to: '/aggregate-members',
        icon: 'users',
        description: 'Lab /aggregateMembers — Aggregate console groups',
      },
    ],
  },
  {
    id: 'exposure',
    label: 'Book Management',
    items: [
      {
        id: 'net-exposure',
        label: 'Net Exposure',
        to: '/net-exposure',
        icon: 'net-exposure',
        description: 'Open books · My PT / Total Book · Detail Book',
      },
      {
        id: 'betfair-net-exposure',
        label: 'Betfair Net Exposure',
        to: '/betfair-net-exposure',
        icon: 'net-exposure',
        description: 'Lab Betfair Net Exposure — exchange books (netExposureNew)',
      },
      {
        id: 'betfair-balance-dashboard',
        label: 'Betfair Balance Dashboard',
        to: '/betfair-balance-dashboard',
        icon: 'activity',
        description: 'Lab Betfair Balance — BF bets · stake · event NE',
      },
      {
        id: 'betfair-bet-ticker',
        label: 'Betfair Bet Ticker',
        to: '/betfair-bet-ticker',
        icon: 'activity',
        description: 'Lab Betfair Bet Ticker — exchange-placed BF bets',
      },
      {
        id: 'bet-ticker',
        label: 'Bet Ticker',
        to: '/bet-ticker',
        icon: 'activity',
        description: 'Open sportsbook bets · newest first',
      },
      {
        id: 'master-bet-ticker',
        label: 'Master Bet Ticker',
        to: '/master-bet-ticker',
        icon: 'activity',
        description: 'Lab Master Bet Ticker — sportsbook bets · Master chrome',
        // ponytail: BetTickerMasterPage kept; rail hidden; URL redirects via App Navigate.
        hiddenFromNav: true,
      },
      {
        id: 'bet-ticker-previous',
        label: 'Bet Ticker Previous',
        to: '/bet-ticker-previous',
        icon: 'clock',
        description:
          'Settled / cancelled sportsbook bets · PG ≤7 days ending today, else BigQuery',
      },
      {
        // ponytail: page merged into Bet Ticker Previous; URL redirects via App Navigate.
        id: 'bet-ticker-previous-new',
        label: 'Bet Ticker Previous New',
        to: '/bet-ticker-previous-new',
        icon: 'clock',
        description: 'Redirects to Bet Ticker Previous',
        hiddenFromNav: true,
      },
      {
        id: 'bet-ticker-flag-final',
        label: 'Bet Ticker Flag Final',
        to: '/bet-ticker-flag-final',
        icon: 'activity',
        description: 'Finally flagged settled sportsbook bets',
      },
    ],
  },
  {
    id: 'catalog',
    label: 'Catalog',
    items: [
      {
        id: 'manual',
        label: 'Manual catalog',
        to: '/catalog/manual',
        icon: 'activity',
        description: 'Search · fetch events/markets · enable',
      },
      {
        id: 'browse',
        label: 'Browse catalog',
        to: '/catalog/browse',
        icon: 'sport',
        description: 'Read-only league → event → markets',
      },
      {
        id: 'automation',
        label: 'Automation',
        to: '/catalog/automation',
        icon: 'settings',
        description: 'Auto-fetch on/off · enable horizon hours',
      },
      {
        id: 'competition',
        label: 'Competition selection',
        to: '/catalog/competition',
        icon: 'sport',
        description: 'Flag leagues · limits · cricket market offer groups',
      },
      {
        id: 'betfair-fetch',
        label: 'Betfair Market Fetch',
        to: '/catalog/betfair-fetch',
        icon: 'activity',
        description: 'Exchange search · fetch · update start time',
      },
      {
        id: 'market-selection',
        label: 'Market selection',
        to: '/catalog/market-selection',
        icon: 'sport',
        description: 'All market types · limits · scorecard · live TV',
      },
    ],
  },
  {
    id: 'reports',
    label: 'Account Statement',
    items: [
      {
        id: 'my-pnl',
        label: 'My P/L',
        to: '/reports/my-pnl',
        icon: 'pnl',
        description: 'Settled book P/L — Lab master-dashboard-new',
      },
      {
        id: 'pl-statement',
        label: 'PL Statement',
        to: '/reports/pl-statement',
        icon: 'pnl',
        description: 'Settled markets with Member / Master / SM / DM / TM net — Lab PLStatement',
      },
      {
        id: 'transfer-statement',
        label: 'Transfer Statement',
        to: '/reports/transfer-statement',
        icon: 'transfers',
        description: 'Give/take credit list — Lab viewTransactionNew',
      },
    ],
  },
  {
    id: 'commission',
    label: 'Commission',
    items: [
      {
        id: 'commission-report',
        label: 'Commission Report',
        to: '/commission-report',
        icon: 'commission',
        description: 'Eligible / Ineligible players by week — Lab commission-report',
      },
    ],
  },
  {
    id: 'surveillance',
    label: 'Surveillance',
    items: [
      {
        id: 'surveillance-live',
        label: 'Surveillance',
        to: '/surveillance',
        icon: 'search',
        description:
          'Settled exchange bets on odds timeline — Postgres ≤7d ending today, else BQ',
      },
    ],
  },
  {
    id: 'vendor-dashboard',
    label: 'Vendor Dashboard',
    items: [
      {
        id: 'card-game',
        label: 'Card Game',
        to: '/vendor-dashboard/card-game',
        icon: 'sport',
        description: 'Card Game vendor dashboard',
      },
      {
        id: 'live-casino',
        label: 'Live Casino',
        to: '/vendor-dashboard/live-casino',
        icon: 'live',
        description: 'Live Casino vendor dashboard',
      },
      {
        id: 'betfair',
        label: 'Betfair',
        to: '/vendor-dashboard/betfair',
        icon: 'sport',
        description: 'Betfair vendor dashboard',
      },
      {
        id: 'instant-games',
        label: 'Instant Games',
        to: '/vendor-dashboard/instant-games',
        icon: 'sport',
        description: 'Instant Games vendor dashboard',
      },
    ],
  },
  {
    id: 'bet-settings',
    label: 'Limit Setting',
    items: [
      {
        id: 'max-limits',
        label: 'Max Limits',
        to: '/bet-settings/max-limits',
        icon: 'settings',
        description: 'Lab EventTypeMaxLimit — sport min/max · fancy/BM caps',
      },
      {
        id: 'max-limit-logs',
        label: 'Max Limit Logs',
        to: '/bet-settings/max-limit-logs',
        icon: 'activity',
        description: 'Lab EventTypeMaxLimitLog — who changed caps',
      },
      {
        id: 'volume-percent',
        label: 'Volume Percent New',
        to: '/bet-settings/volume-percent',
        icon: 'activity',
        description: 'TM place % · min stake · active rate',
      },
    ],
  },
  {
    id: 'logs',
    label: 'Logs',
    items: [
      {
        id: 'logs-cl-transfers',
        label: 'CL Transfers',
        to: '/logs/cl-transfers',
        icon: 'sliders',
        description: 'Credit-limit changes across the hierarchy',
      },
      {
        id: 'betfair-rates-logs',
        label: 'Betfair Rates Logs',
        to: '/logs/betfair-rates-logs',
        icon: 'activity',
        description: 'Lab BetfairRatesLogs — who changed TM place %',
      },
      {
        id: 'invalid-bets',
        label: 'Invalid Bets',
        to: '/logs/invalid-bets',
        icon: 'activity',
        description: 'Lab Invalid Bets — VARBet + Invalid Bets logs',
      },
    ],
  },
  {
    id: 'security',
    label: 'Security',
    icon: 'shield',
    items: [
      {
        id: 'cm-ip-whitelist',
        label: 'CM IP whitelist',
        to: '/security/ip-whitelist',
        icon: 'settings',
        description: 'Operator IPs allowed to call CM-API',
      },
      {
        id: 'blocked-ip',
        label: 'Blocked IP',
        to: '/security/blocked-ip',
        icon: 'settings',
        description: 'Lab /blocked-ip',
      },
      {
        id: 'login-from-blocked-ip',
        label: 'Login from blocked IP',
        to: '/security/login-from-blocked-ip',
        icon: 'activity',
        description: 'Lab /login-from-blocked-ip',
      },
      {
        id: 'whitelist-users',
        label: 'Whitelist users',
        to: '/security/whitelist-users',
        icon: 'user',
        description: 'Lab /whitelist-users — IP or device exceptions',
      },
      {
        id: 'blocked-device-ids',
        label: 'Blocked device IDs',
        to: '/security/blocked-device-ids',
        icon: 'settings',
        description: 'Lab /blocked-device-ids',
      },
      {
        id: 'all-cheaters',
        label: 'All cheaters',
        to: '/security/all-cheaters',
        icon: 'user',
        description: 'Lab /all-cheaters — flag sniping / matching players',
      },
      {
        id: 'odds-violation-whitelist',
        label: 'Odds Violation Whitelist',
        to: '/security/odds-violation-whitelist',
        icon: 'settings',
        description:
          'Lab /odds-violation-whitelist — skip odds-violation lock for players',
      },
      {
        id: 'manual-group-betting',
        label: 'Manual Group Betting',
        to: '/security/manual-group-betting',
        icon: 'downline',
        description: 'Lab /manualGroupBetting — track suspected syndicate groups',
      },
      {
        id: 'manage-ip',
        label: 'Multiple IPs',
        to: '/security/manage-ip',
        icon: 'settings',
        description: 'Lab /duplicate-ip — IPs shared by ≥2 players',
      },
      {
        id: 'multiple-device-ids',
        label: 'Multiple device IDs',
        to: '/security/multiple-device-ids',
        icon: 'downline',
        description: 'Lab /multiple-device-ids',
      },
      {
        id: 'fprint-multiple-device-ids',
        label: 'Fingerprint multiple devices',
        to: '/security/fprint-multiple-device-ids',
        icon: 'downline',
        description: 'Lab /fprint-multiple-device-ids',
      },
      {
        id: 'console-2fa',
        label: 'Console 2FA',
        to: '/security/console-2fa',
        icon: 'shield',
        description: 'CM owner — agent (Keycloak) + portal player 2FA on/off',
      },
    ],
  },
]
